import { useMemo } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useT } from '@/i18n/provider';
import { TableOfContents } from '@/components/TableOfContents';
import { highlightLpql } from '@/lib/lpql-highlight';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function LpqlBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="p-4 bg-gray-950 text-gray-100 text-sm overflow-x-auto font-mono leading-relaxed">
        <code>{highlightLpql(children.trim())}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };

  return (
    <div className={`my-4 rounded-xl border-l-4 px-4 py-3 ${styles[type]}`}>
      <span className="font-semibold text-sm">{labels[type]}:</span>{' '}
      <span className="text-sm">{children}</span>
    </div>
  );
}

// ── SVG Icons for Architecture Diagram ──────────────────────────

function IconPod({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function IconFile({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="15" y2="17" />
    </svg>
  );
}

function IconCollector({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9" />
      <path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4" />
      <circle cx="12" cy="12" r="2" />
      <path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4" />
      <path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1" />
    </svg>
  );
}

function IconKubernetes({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="1" />
      <line x1="12" y1="3" x2="12" y2="7" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <line x1="3.5" y1="7.5" x2="7" y2="9.5" />
      <line x1="17" y1="14.5" x2="20.5" y2="16.5" />
      <line x1="3.5" y1="16.5" x2="7" y2="14.5" />
      <line x1="17" y1="9.5" x2="20.5" y2="7.5" />
    </svg>
  );
}

function IconPulse({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.3" />
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="7" opacity="0.5" />
      <circle cx="12" cy="12" r="10" opacity="0.25" />
    </svg>
  );
}

const archIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  pod: IconPod,
  file: IconFile,
  collector: IconCollector,
  kubernetes: IconKubernetes,
  pulse: IconPulse,
};

// ── ReactFlow Architecture Diagram ──────────────────────────────

type K8sNodeData = {
  label: string;
  sublabel?: string;
  color: string;
  icon?: string;
  w?: number; // fixed width for consistent alignment
};

function K8sArchNode({ data }: NodeProps<Node<K8sNodeData>>) {
  const borderColor: Record<string, string> = {
    blue: 'border-blue-500/60',
    purple: 'border-primary-500/60',
    green: 'border-emerald-500/60',
    amber: 'border-amber-500/60',
    gray: 'border-gray-600/60',
  };

  const textColor: Record<string, string> = {
    blue: 'text-blue-400',
    purple: 'text-primary-400',
    green: 'text-emerald-400',
    amber: 'text-amber-400',
    gray: 'text-gray-400',
  };

  const iconColor: Record<string, string> = {
    blue: 'text-blue-400/70',
    purple: 'text-primary-400/70',
    green: 'text-emerald-400/70',
    amber: 'text-amber-400/70',
    gray: 'text-gray-500/70',
  };

  const Icon = data.icon ? archIcons[data.icon] : null;

  return (
    <div
      className={`rounded-lg border-2 ${borderColor[data.color]} bg-gray-900/90 px-5 py-3 text-center shadow-lg`}
      style={data.w ? { width: data.w } : undefined}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-600 !w-2 !h-2 !border-0" />
      {Icon && (
        <div className="flex justify-center mb-1">
          <Icon className={`w-4 h-4 ${iconColor[data.color]}`} />
        </div>
      )}
      <div className={`text-xs font-semibold ${textColor[data.color]}`}>{data.label}</div>
      {data.sublabel && <div className="text-[10px] text-gray-500 mt-0.5 whitespace-nowrap">{data.sublabel}</div>}
      <Handle type="source" position={Position.Bottom} className="!bg-gray-600 !w-2 !h-2 !border-0" />
    </div>
  );
}

const k8sNodeTypes = { k8sNode: K8sArchNode as unknown as React.ComponentType<NodeProps> };

// ── Layout ──────────────────────────────────────────────────────
// All positions are CENTER points, nodeOrigin={[0.5, 0.5]}
// makes ReactFlow position nodes from their center.
//
// Fixed node widths per type:
const W_POD = 130;
const W_LOG = 180;
const W_OTEL = 200;
const W_API = 280;
const W_LP = 240;

// 3 groups spaced evenly, center x of each group
const GROUP_SPACING = 360;
const CENTER_X = 540;  // diagram center
const G1 = CENTER_X - GROUP_SPACING;  // 180
const G2 = CENTER_X;                   // 540
const G3 = CENTER_X + GROUP_SPACING;  // 900

// Pod offset from group center (two pods per group)
const POD_OFFSET = 85;

// Row y positions (centers) with generous spacing
const Y_PODS = 35;
const Y_LOGS = 170;
const Y_OTEL = 310;
const Y_API = 470;
const Y_LP = 600;

const archNodes: Node<K8sNodeData>[] = [
  // ── Pods row (2 per group, offset left/right from group center) ──
  { id: 'pod-a', type: 'k8sNode', position: { x: G1 - POD_OFFSET, y: Y_PODS }, data: { label: 'Pod A', sublabel: 'api-server', color: 'blue', icon: 'pod', w: W_POD } },
  { id: 'pod-b', type: 'k8sNode', position: { x: G1 + POD_OFFSET, y: Y_PODS }, data: { label: 'Pod B', sublabel: 'payment-svc', color: 'blue', icon: 'pod', w: W_POD } },
  { id: 'pod-c', type: 'k8sNode', position: { x: G2 - POD_OFFSET, y: Y_PODS }, data: { label: 'Pod C', sublabel: 'frontend', color: 'blue', icon: 'pod', w: W_POD } },
  { id: 'pod-d', type: 'k8sNode', position: { x: G2 + POD_OFFSET, y: Y_PODS }, data: { label: 'Pod D', sublabel: 'worker', color: 'blue', icon: 'pod', w: W_POD } },
  { id: 'pod-e', type: 'k8sNode', position: { x: G3 - POD_OFFSET, y: Y_PODS }, data: { label: 'Pod E', sublabel: 'ingress', color: 'blue', icon: 'pod', w: W_POD } },
  { id: 'pod-f', type: 'k8sNode', position: { x: G3 + POD_OFFSET, y: Y_PODS }, data: { label: 'Pod F', sublabel: 'cache', color: 'blue', icon: 'pod', w: W_POD } },

  // ── Log files (centered on group) ──
  { id: 'logs-1', type: 'k8sNode', position: { x: G1, y: Y_LOGS }, data: { label: '/var/log/containers/', sublabel: 'Node 1', color: 'gray', icon: 'file', w: W_LOG } },
  { id: 'logs-2', type: 'k8sNode', position: { x: G2, y: Y_LOGS }, data: { label: '/var/log/containers/', sublabel: 'Node 2', color: 'gray', icon: 'file', w: W_LOG } },
  { id: 'logs-3', type: 'k8sNode', position: { x: G3, y: Y_LOGS }, data: { label: '/var/log/containers/', sublabel: 'Node 3', color: 'gray', icon: 'file', w: W_LOG } },

  // ── OTel Collectors (centered on group) ──
  { id: 'otel-1', type: 'k8sNode', position: { x: G1, y: Y_OTEL }, data: { label: 'OTel Collector', sublabel: 'filelog \u2192 k8sattr \u2192 batch', color: 'purple', icon: 'collector', w: W_OTEL } },
  { id: 'otel-2', type: 'k8sNode', position: { x: G2, y: Y_OTEL }, data: { label: 'OTel Collector', sublabel: 'filelog \u2192 k8sattr \u2192 batch', color: 'purple', icon: 'collector', w: W_OTEL } },
  { id: 'otel-3', type: 'k8sNode', position: { x: G3, y: Y_OTEL }, data: { label: 'OTel Collector', sublabel: 'filelog \u2192 k8sattr \u2192 batch', color: 'purple', icon: 'collector', w: W_OTEL } },

  // ── Kubernetes API Server (centered on diagram) ──
  { id: 'k8s-api', type: 'k8sNode', position: { x: CENTER_X, y: Y_API }, data: { label: 'Kubernetes API Server', sublabel: 'metadata enrichment (pods, labels, nodes)', color: 'amber', icon: 'kubernetes', w: W_API } },

  // ── LogPulse (centered on diagram) ──
  { id: 'logpulse', type: 'k8sNode', position: { x: CENTER_X, y: Y_LP }, data: { label: 'LogPulse OTLP API', sublabel: '/v1/logs \u00B7 gzip \u00B7 Bearer auth', color: 'green', icon: 'pulse', w: W_LP } },
];

const archEdges: Edge[] = [
  // Pods → Logs
  { id: 'pa-l1', source: 'pod-a', target: 'logs-1', type: 'smoothstep', style: { stroke: '#3B82F6', strokeWidth: 1.5, opacity: 0.5 } },
  { id: 'pb-l1', source: 'pod-b', target: 'logs-1', type: 'smoothstep', style: { stroke: '#3B82F6', strokeWidth: 1.5, opacity: 0.5 } },
  { id: 'pc-l2', source: 'pod-c', target: 'logs-2', type: 'smoothstep', style: { stroke: '#3B82F6', strokeWidth: 1.5, opacity: 0.5 } },
  { id: 'pd-l2', source: 'pod-d', target: 'logs-2', type: 'smoothstep', style: { stroke: '#3B82F6', strokeWidth: 1.5, opacity: 0.5 } },
  { id: 'pe-l3', source: 'pod-e', target: 'logs-3', type: 'smoothstep', style: { stroke: '#3B82F6', strokeWidth: 1.5, opacity: 0.5 } },
  { id: 'pf-l3', source: 'pod-f', target: 'logs-3', type: 'smoothstep', style: { stroke: '#3B82F6', strokeWidth: 1.5, opacity: 0.5 } },

  // Logs → OTel
  { id: 'l1-o1', source: 'logs-1', target: 'otel-1', type: 'smoothstep', style: { stroke: '#6B7280', strokeWidth: 1.5 } },
  { id: 'l2-o2', source: 'logs-2', target: 'otel-2', type: 'smoothstep', style: { stroke: '#6B7280', strokeWidth: 1.5 } },
  { id: 'l3-o3', source: 'logs-3', target: 'otel-3', type: 'smoothstep', style: { stroke: '#6B7280', strokeWidth: 1.5 } },

  // OTel → K8s API (dashed, metadata lookup)
  { id: 'o1-api', source: 'otel-1', target: 'k8s-api', type: 'smoothstep', style: { stroke: '#F59E0B', strokeWidth: 1.5, strokeDasharray: '6 3' } },
  { id: 'o2-api', source: 'otel-2', target: 'k8s-api', type: 'smoothstep', style: { stroke: '#F59E0B', strokeWidth: 1.5, strokeDasharray: '6 3' } },
  { id: 'o3-api', source: 'otel-3', target: 'k8s-api', type: 'smoothstep', style: { stroke: '#F59E0B', strokeWidth: 1.5, strokeDasharray: '6 3' } },

  // K8s API → LogPulse
  { id: 'api-lp', source: 'k8s-api', target: 'logpulse', type: 'smoothstep', style: { stroke: '#10B981', strokeWidth: 2 }, label: 'OTLP HTTP', labelStyle: { fill: '#6B7280', fontSize: 10 }, labelBgStyle: { fill: '#0B1120', fillOpacity: 0.8 } },
];

function ArchitectureDiagram() {
  const noop = useMemo(() => () => {}, []);
  const nodeOrigin = useMemo<[number, number]>(() => [0.5, 0.5], []);
  return (
    <div className="my-6 h-[620px] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden bg-gray-950">
      <ReactFlow
        nodes={archNodes}
        edges={archEdges}
        nodeTypes={k8sNodeTypes}
        nodeOrigin={nodeOrigin}
        fitView
        fitViewOptions={{ maxZoom: 0.75, padding: 0.15 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        onNodeClick={noop}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#1D2939" />
      </ReactFlow>
    </div>
  );
}

function StepNumber({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-bold mr-2 shrink-0">
      {n}
    </span>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'architecture', label: 'Architecture' },
  { id: 'prerequisites', label: 'Prerequisites' },
  { id: 'helm-install', label: 'Helm Installation' },
  { id: 'helm-values', label: 'Configuration Values', level: 1 },
  { id: 'quick-start', label: 'Quick Start', level: 1 },
  { id: 'namespace-filtering', label: 'Namespace Filtering', level: 1 },
  { id: 'k8s-metadata', label: 'Kubernetes Metadata' },
  { id: 'metadata-fields', label: 'Metadata Fields', level: 1 },
  { id: 'labels-annotations', label: 'Labels & Annotations', level: 1 },
  { id: 'otel-collector', label: 'OTel Collector' },
  { id: 'otel-pipeline', label: 'Pipeline Config', level: 1 },
  { id: 'k8sattributes', label: 'k8sattributes Processor', level: 1 },
  { id: 'rbac', label: 'RBAC Permissions' },
  { id: 'daemonset', label: 'DaemonSet Details' },
  { id: 'volumes', label: 'Volume Mounts', level: 1 },
  { id: 'env-vars', label: 'Environment Variables', level: 1 },
  { id: 'health-checks', label: 'Health Checks', level: 1 },
  { id: 'security', label: 'Security' },
  { id: 'querying', label: 'Querying K8s Logs' },
  { id: 'lpql-examples', label: 'LPQL Examples', level: 1 },
  { id: 'advanced', label: 'Advanced Config' },
  { id: 'custom-values', label: 'Custom Values', level: 1 },
  { id: 'resources', label: 'Resource Tuning', level: 1 },
  { id: 'tolerations', label: 'Tolerations & Affinity', level: 1 },
  { id: 'vector-alternative', label: 'Vector Alternative' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
  { id: 'uninstall', label: 'Uninstalling' },
];

export function KubernetesPage() {
  useDocumentMeta({ title: 'Kubernetes Integration: LogPulse Docs', description: 'Deploy LogPulse on Kubernetes with Helm charts, DaemonSets, and OpenTelemetry Collector.' });

  const t = useT();

  return (
    <div className="flex gap-8">
      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1">
            {t('docs.nav.integrations')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            {t('docs.k8s.title')}
          </h1>
          <P>{t('docs.k8s.intro')}</P>
        </div>

        {/* Overview */}
        <Section id="overview" title={t('docs.k8s.overview.title')}>
          <P>{t('docs.k8s.overview.description')}</P>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-6">
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">Helm</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.k8s.overview.helmLabel')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-info-600 dark:text-info-400 mb-1">OTel</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.k8s.overview.otelLabel')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-success-600 dark:text-success-400 mb-1">DaemonSet</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.k8s.overview.daemonsetLabel')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-warning-600 dark:text-warning-400 mb-1">RBAC</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.k8s.overview.rbacLabel')}</div>
            </div>
          </div>
        </Section>

        {/* Architecture */}
        <Section id="architecture" title={t('docs.k8s.architecture.title')}>
          <P>{t('docs.k8s.architecture.description')}</P>
          <ArchitectureDiagram />
          <P>{t('docs.k8s.architecture.flow')}</P>
          <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1.5 mb-4 ml-1">
            <li>{t('docs.k8s.architecture.step1')}</li>
            <li>{t('docs.k8s.architecture.step2')}</li>
            <li>{t('docs.k8s.architecture.step3')}</li>
            <li>{t('docs.k8s.architecture.step4')}</li>
            <li>{t('docs.k8s.architecture.step5')}</li>
          </ul>
        </Section>

        {/* Prerequisites */}
        <Section id="prerequisites" title={t('docs.k8s.prerequisites.title')}>
          <P>{t('docs.k8s.prerequisites.description')}</P>
          <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1.5 mb-4 ml-1">
            <li>{t('docs.k8s.prerequisites.k8sVersion')}</li>
            <li>{t('docs.k8s.prerequisites.helm')}</li>
            <li>{t('docs.k8s.prerequisites.kubectl')}</li>
            <li>{t('docs.k8s.prerequisites.apiKey')}</li>
            <li>{t('docs.k8s.prerequisites.rbac')}</li>
          </ul>
          <Callout type="info">{t('docs.k8s.prerequisites.note')}</Callout>
        </Section>

        {/* Helm Installation */}
        <Section id="helm-install" title={t('docs.k8s.helm.title')}>
          <P>{t('docs.k8s.helm.description')}</P>

          <SubSection id="quick-start" title={t('docs.k8s.helm.quickStart.title')}>
            <P>{t('docs.k8s.helm.quickStart.description')}</P>
            <div className="space-y-4">
              <div>
                <div className="flex items-center mb-2">
                  <StepNumber n={1} />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{t('docs.k8s.helm.quickStart.step1')}</span>
                </div>
                <CodeBlock title="Add Helm repository">{`helm repo add logpulse https://charts.logpulse.io
helm repo update`}</CodeBlock>
              </div>
              <div>
                <div className="flex items-center mb-2">
                  <StepNumber n={2} />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{t('docs.k8s.helm.quickStart.step2')}</span>
                </div>
                <CodeBlock title="Create namespace">{`kubectl create namespace logpulse`}</CodeBlock>
              </div>
              <div>
                <div className="flex items-center mb-2">
                  <StepNumber n={3} />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{t('docs.k8s.helm.quickStart.step3')}</span>
                </div>
                <CodeBlock title="Install the LogPulse agent">{`helm install logpulse-agent logpulse/logpulse-agent \\
  --namespace logpulse \\
  --set apiKey="lp_your_api_key_here" \\
  --set cluster.name="my-production-cluster"`}</CodeBlock>
              </div>
              <div>
                <div className="flex items-center mb-2">
                  <StepNumber n={4} />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{t('docs.k8s.helm.quickStart.step4')}</span>
                </div>
                <CodeBlock title="Verify the installation">{`# Check DaemonSet status
kubectl get daemonset -n logpulse

# Check pods are running on each node
kubectl get pods -n logpulse -o wide

# View collector logs
kubectl logs -n logpulse -l app.kubernetes.io/name=logpulse-agent --tail=50`}</CodeBlock>
              </div>
            </div>
            <Callout type="tip">{t('docs.k8s.helm.quickStart.tip')}</Callout>
          </SubSection>

          <SubSection id="helm-values" title={t('docs.k8s.helm.values.title')}>
            <P>{t('docs.k8s.helm.values.description')}</P>
            <Table
              headers={['Parameter', 'Default', 'Description']}
              rows={[
                ['apiKey', '""', t('docs.k8s.helm.values.apiKey')],
                ['cluster.name', '"production"', t('docs.k8s.helm.values.clusterName')],
                ['endpoint', '"https://api.logpulse.io"', t('docs.k8s.helm.values.endpoint')],
                ['image.repository', '"otel/opentelemetry-collector-contrib"', t('docs.k8s.helm.values.imageRepo')],
                ['image.tag', '"0.96.0"', t('docs.k8s.helm.values.imageTag')],
                ['resources.limits.cpu', '"200m"', t('docs.k8s.helm.values.cpuLimit')],
                ['resources.limits.memory', '"256Mi"', t('docs.k8s.helm.values.memLimit')],
                ['resources.requests.cpu', '"50m"', t('docs.k8s.helm.values.cpuReq')],
                ['resources.requests.memory', '"128Mi"', t('docs.k8s.helm.values.memReq')],
                ['logs.excludeNamespaces', '[kube-system, ...]', t('docs.k8s.helm.values.exclude')],
                ['logs.includeNamespaces', '[]', t('docs.k8s.helm.values.include')],
                ['logs.containerLogPath', '"/var/log/containers"', t('docs.k8s.helm.values.logPath')],
                ['logs.includePodLabels', 'true', t('docs.k8s.helm.values.podLabels')],
                ['logs.includePodAnnotations', 'false', t('docs.k8s.helm.values.podAnnotations')],
                ['serviceAccount.create', 'true', t('docs.k8s.helm.values.saCreate')],
                ['healthCheck.port', '13133', t('docs.k8s.helm.values.healthPort')],
              ]}
            />
          </SubSection>

          <SubSection id="namespace-filtering" title={t('docs.k8s.helm.namespaces.title')}>
            <P>{t('docs.k8s.helm.namespaces.description')}</P>
            <CodeBlock title="Exclude specific namespaces">{`helm install logpulse-agent logpulse/logpulse-agent \\
  --namespace logpulse \\
  --set apiKey="lp_your_key" \\
  --set logs.excludeNamespaces[0]=kube-system \\
  --set logs.excludeNamespaces[1]=kube-public \\
  --set logs.excludeNamespaces[2]=kube-node-lease \\
  --set logs.excludeNamespaces[3]=monitoring \\
  --set logs.excludeNamespaces[4]=logpulse`}</CodeBlock>
            <P>{t('docs.k8s.helm.namespaces.includeDesc')}</P>
            <CodeBlock title="Include only specific namespaces">{`# Only collect logs from 'production' and 'staging' namespaces
helm install logpulse-agent logpulse/logpulse-agent \\
  --namespace logpulse \\
  --set apiKey="lp_your_key" \\
  --set logs.includeNamespaces[0]=production \\
  --set logs.includeNamespaces[1]=staging`}</CodeBlock>
            <Callout type="warning">{t('docs.k8s.helm.namespaces.warning')}</Callout>
          </SubSection>
        </Section>

        {/* Kubernetes Metadata */}
        <Section id="k8s-metadata" title={t('docs.k8s.metadata.title')}>
          <P>{t('docs.k8s.metadata.description')}</P>

          <SubSection id="metadata-fields" title={t('docs.k8s.metadata.fields.title')}>
            <P>{t('docs.k8s.metadata.fields.description')}</P>
            <Table
              headers={['Field', 'OTLP Attribute', 'Description']}
              rows={[
                ['cluster', 'k8s.cluster.name', t('docs.k8s.metadata.fields.cluster')],
                ['namespace', 'k8s.namespace.name', t('docs.k8s.metadata.fields.namespace')],
                ['pod', 'k8s.pod.name', t('docs.k8s.metadata.fields.pod')],
                ['container', 'k8s.container.name', t('docs.k8s.metadata.fields.container')],
                ['node', 'k8s.node.name', t('docs.k8s.metadata.fields.node')],
                ['labels', 'k8s.pod.labels.*', t('docs.k8s.metadata.fields.labels')],
                ['annotations', 'k8s.pod.annotation.*', t('docs.k8s.metadata.fields.annotations')],
              ]}
            />
            <Callout type="info">{t('docs.k8s.metadata.fields.note')}</Callout>
          </SubSection>

          <SubSection id="labels-annotations" title={t('docs.k8s.metadata.labelsAnnotations.title')}>
            <P>{t('docs.k8s.metadata.labelsAnnotations.description')}</P>
            <CodeBlock title="values.yaml: Enable pod annotations">{`logs:
  includePodLabels: true       # Enabled by default
  includePodAnnotations: true  # Disabled by default, enable if needed`}</CodeBlock>
            <P>{t('docs.k8s.metadata.labelsAnnotations.queryExample')}</P>
            <LpqlBlock title="LPQL: Query by pod label">{`labels.app="payment-service" level="error"
| stats count by pod, container`}</LpqlBlock>
            <Callout type="tip">{t('docs.k8s.metadata.labelsAnnotations.tip')}</Callout>
          </SubSection>
        </Section>

        {/* OpenTelemetry Collector */}
        <Section id="otel-collector" title={t('docs.k8s.otel.title')}>
          <P>{t('docs.k8s.otel.description')}</P>

          <SubSection id="otel-pipeline" title={t('docs.k8s.otel.pipeline.title')}>
            <P>{t('docs.k8s.otel.pipeline.description')}</P>
            <CodeBlock title="OTel Collector Pipeline (auto-generated by Helm chart)">{`receivers:
  filelog:
    include:
      - /var/log/containers/*.log
    exclude:
      - /var/log/containers/*_kube-system_*.log
      - /var/log/containers/*_kube-public_*.log
      - /var/log/containers/*_kube-node-lease_*.log
    start_at: end
    include_file_path: true
    operators:
      # Parse container log format: <timestamp> <stream> <flags> <log>
      - type: regex_parser
        id: container_parser
        regex: '^(?P<time>[^ ]+) (?P<stream>stdout|stderr) (?P<logtag>[^ ]*) (?P<log>.*)$'
        timestamp:
          parse_from: attributes.time
          layout: '%Y-%m-%dT%H:%M:%S.%fZ'
      # Parse Kubernetes metadata from filename
      - type: regex_parser
        id: k8s_filename_parser
        regex: '^.*\\/(?P<pod_name>[^_]+)_(?P<namespace>[^_]+)_(?P<container_name>[^-]+)-(?P<container_id>[^.]+)\\.log$'
        parse_from: attributes["log.file.path"]
      # Move parsed log to body
      - type: move
        from: attributes.log
        to: body

processors:
  k8sattributes:
    auth_type: "serviceAccount"
    passthrough: false
    extract:
      metadata:
        - k8s.namespace.name
        - k8s.pod.name
        - k8s.pod.uid
        - k8s.deployment.name
        - k8s.daemonset.name
        - k8s.statefulset.name
        - k8s.job.name
        - k8s.cronjob.name
        - k8s.node.name
        - k8s.container.name
      labels:
        - tag_name: k8s.pod.labels.$$1
          key_regex: (.*)
          from: pod
    pod_association:
      - sources:
          - from: resource_attribute
            name: k8s.pod.name
          - from: resource_attribute
            name: k8s.namespace.name

  resource:
    attributes:
      - key: k8s.cluster.name
        value: "my-cluster"
        action: upsert

  batch:
    timeout: 5s
    send_batch_size: 1000

exporters:
  otlphttp:
    endpoint: https://api.logpulse.io/v1/logs
    headers:
      Authorization: "Bearer \${env:LOGPULSE_API_KEY}"
    compression: gzip
    retry_on_failure:
      enabled: true
      initial_interval: 1s
      max_interval: 30s
      max_elapsed_time: 300s

extensions:
  health_check:
    endpoint: 0.0.0.0:13133

service:
  extensions: [health_check]
  pipelines:
    logs:
      receivers: [filelog]
      processors: [k8sattributes, resource, batch]
      exporters: [otlphttp]`}</CodeBlock>
          </SubSection>

          <SubSection id="k8sattributes" title={t('docs.k8s.otel.k8sattr.title')}>
            <P>{t('docs.k8s.otel.k8sattr.description')}</P>
            <P>{t('docs.k8s.otel.k8sattr.metadataList')}</P>
            <Table
              headers={['Metadata', 'Description']}
              rows={[
                ['k8s.namespace.name', t('docs.k8s.otel.k8sattr.ns')],
                ['k8s.pod.name', t('docs.k8s.otel.k8sattr.podName')],
                ['k8s.pod.uid', t('docs.k8s.otel.k8sattr.podUid')],
                ['k8s.deployment.name', t('docs.k8s.otel.k8sattr.deployment')],
                ['k8s.daemonset.name', t('docs.k8s.otel.k8sattr.daemonset')],
                ['k8s.statefulset.name', t('docs.k8s.otel.k8sattr.statefulset')],
                ['k8s.job.name', t('docs.k8s.otel.k8sattr.job')],
                ['k8s.cronjob.name', t('docs.k8s.otel.k8sattr.cronjob')],
                ['k8s.node.name', t('docs.k8s.otel.k8sattr.node')],
                ['k8s.container.name', t('docs.k8s.otel.k8sattr.container')],
              ]}
            />
            <Callout type="info">{t('docs.k8s.otel.k8sattr.note')}</Callout>
          </SubSection>
        </Section>

        {/* RBAC */}
        <Section id="rbac" title={t('docs.k8s.rbac.title')}>
          <P>{t('docs.k8s.rbac.description')}</P>
          <CodeBlock title="ClusterRole (auto-created by Helm chart)">{`apiVersion: rbac.authorization.k8s.io/v1
kind: ClusterRole
metadata:
  name: logpulse-agent
rules:
  # Required for k8sattributes processor
  - apiGroups: [""]
    resources:
      - pods
      - namespaces
      - nodes
    verbs: ["get", "watch", "list"]
  - apiGroups: ["apps"]
    resources:
      - replicasets
      - deployments
      - daemonsets
      - statefulsets
    verbs: ["get", "watch", "list"]
  - apiGroups: ["batch"]
    resources:
      - jobs
      - cronjobs
    verbs: ["get", "watch", "list"]`}</CodeBlock>
          <P>{t('docs.k8s.rbac.explanation')}</P>
          <Table
            headers={['API Group', 'Resources', 'Verbs', 'Reason']}
            rows={[
              ['""  (core)', 'pods, namespaces, nodes', 'get, watch, list', t('docs.k8s.rbac.coreReason')],
              ['apps', 'replicasets, deployments, daemonsets, statefulsets', 'get, watch, list', t('docs.k8s.rbac.appsReason')],
              ['batch', 'jobs, cronjobs', 'get, watch, list', t('docs.k8s.rbac.batchReason')],
            ]}
          />
          <Callout type="tip">{t('docs.k8s.rbac.tip')}</Callout>
        </Section>

        {/* DaemonSet Details */}
        <Section id="daemonset" title={t('docs.k8s.daemonset.title')}>
          <P>{t('docs.k8s.daemonset.description')}</P>

          <SubSection id="volumes" title={t('docs.k8s.daemonset.volumes.title')}>
            <P>{t('docs.k8s.daemonset.volumes.description')}</P>
            <Table
              headers={['Volume', 'Host Path', 'Mount Path', 'Description']}
              rows={[
                ['config', '(ConfigMap)', '/etc/otel-collector', t('docs.k8s.daemonset.volumes.config')],
                ['varlog', '/var/log', '/var/log', t('docs.k8s.daemonset.volumes.varlog')],
                ['varlibdockercontainers', '/var/lib/docker/containers', '/var/lib/docker/containers', t('docs.k8s.daemonset.volumes.docker')],
              ]}
            />
            <Callout type="warning">{t('docs.k8s.daemonset.volumes.warning')}</Callout>
          </SubSection>

          <SubSection id="env-vars" title={t('docs.k8s.daemonset.env.title')}>
            <P>{t('docs.k8s.daemonset.env.description')}</P>
            <Table
              headers={['Variable', 'Source', 'Description']}
              rows={[
                ['LOGPULSE_API_KEY', 'Secret (api-key)', t('docs.k8s.daemonset.env.apiKey')],
                ['OTEL_K8S_NODE_NAME', 'fieldRef: spec.nodeName', t('docs.k8s.daemonset.env.nodeName')],
                ['OTEL_K8S_POD_NAME', 'fieldRef: metadata.name', t('docs.k8s.daemonset.env.podName')],
                ['OTEL_K8S_NAMESPACE', 'fieldRef: metadata.namespace', t('docs.k8s.daemonset.env.namespace')],
              ]}
            />
          </SubSection>

          <SubSection id="health-checks" title={t('docs.k8s.daemonset.health.title')}>
            <P>{t('docs.k8s.daemonset.health.description')}</P>
            <Table
              headers={['Probe', 'Path', 'Port', 'Timing']}
              rows={[
                ['Liveness', '/', '13133', t('docs.k8s.daemonset.health.liveness')],
                ['Readiness', '/', '13133', t('docs.k8s.daemonset.health.readiness')],
              ]}
            />
          </SubSection>
        </Section>

        {/* Security */}
        <Section id="security" title={t('docs.k8s.security.title')}>
          <P>{t('docs.k8s.security.description')}</P>
          <Table
            headers={['Setting', 'Value', 'Description']}
            rows={[
              ['runAsNonRoot', 'false', t('docs.k8s.security.nonRoot')],
              ['runAsUser', '0', t('docs.k8s.security.runAsUser')],
              ['readOnlyRootFilesystem', 'true', t('docs.k8s.security.readOnly')],
              ['privileged', 'false', t('docs.k8s.security.privileged')],
            ]}
          />
          <P>{t('docs.k8s.security.apiKeyNote')}</P>
          <CodeBlock title="API key stored as Kubernetes Secret">{`apiVersion: v1
kind: Secret
metadata:
  name: logpulse-agent-secret
type: Opaque
stringData:
  api-key: "lp_your_api_key_here"`}</CodeBlock>
          <Callout type="warning">{t('docs.k8s.security.secretWarning')}</Callout>
        </Section>

        {/* Querying K8s Logs */}
        <Section id="querying" title={t('docs.k8s.querying.title')}>
          <P>{t('docs.k8s.querying.description')}</P>

          <SubSection id="lpql-examples" title={t('docs.k8s.querying.examples.title')}>
            <P>{t('docs.k8s.querying.examples.description')}</P>
            <LpqlBlock title="Filter by namespace">{`namespace="production" level="error"`}</LpqlBlock>
            <LpqlBlock title="Filter by pod name (wildcard)">{`namespace="production" pod="api-server-*" level="error"
| stats count by pod`}</LpqlBlock>
            <LpqlBlock title="Cross-namespace error overview">{`level="error" cluster="production"
| stats count by namespace, pod
| sort count desc`}</LpqlBlock>
            <LpqlBlock title="Container restarts (OOMKilled, CrashLoopBackOff)">{`"OOMKilled" OR "CrashLoopBackOff" OR "Back-off restarting"
| stats count by namespace, pod, container`}</LpqlBlock>
            <LpqlBlock title="Logs from a specific deployment">{`labels.app="payment-service" namespace="production"
| timechart count by level`}</LpqlBlock>
            <LpqlBlock title="Node-level analysis">{`node="ip-10-0-1-42" level="error"
| stats count by namespace, pod`}</LpqlBlock>
            <LpqlBlock title="Find pods with highest error rates">{`level="error" last 1h
| stats count as errors by namespace, pod
| sort errors desc
| head 20`}</LpqlBlock>
          </SubSection>
        </Section>

        {/* Advanced Configuration */}
        <Section id="advanced" title={t('docs.k8s.advanced.title')}>
          <P>{t('docs.k8s.advanced.description')}</P>

          <SubSection id="custom-values" title={t('docs.k8s.advanced.customValues.title')}>
            <P>{t('docs.k8s.advanced.customValues.description')}</P>
            <CodeBlock title="custom-values.yaml">{`# LogPulse API key (required)
apiKey: "lp_your_api_key_here"

# Cluster identification
cluster:
  name: "production-eu-west-1"

# Custom endpoint (self-hosted)
# endpoint: "https://your-logpulse-instance.internal"

# Collect from specific namespaces only
logs:
  includeNamespaces:
    - production
    - staging
  excludeNamespaces: []
  includePodLabels: true
  includePodAnnotations: true

# Resource tuning for high-volume clusters
resources:
  limits:
    cpu: 500m
    memory: 512Mi
  requests:
    cpu: 100m
    memory: 256Mi

# Run on specific nodes
nodeSelector:
  node-role.kubernetes.io/worker: ""

# Service account annotations (e.g., for IRSA on EKS)
serviceAccount:
  annotations:
    eks.amazonaws.com/role-arn: "arn:aws:iam::123456789:role/logpulse-agent"`}</CodeBlock>
            <CodeBlock title="Install with custom values file">{`helm install logpulse-agent logpulse/logpulse-agent \\
  --namespace logpulse \\
  --create-namespace \\
  -f custom-values.yaml`}</CodeBlock>
          </SubSection>

          <SubSection id="resources" title={t('docs.k8s.advanced.resources.title')}>
            <P>{t('docs.k8s.advanced.resources.description')}</P>
            <Table
              headers={['Cluster Size', 'CPU Limit', 'Memory Limit', 'Notes']}
              rows={[
                [t('docs.k8s.advanced.resources.small'), '200m', '256Mi', t('docs.k8s.advanced.resources.smallNote')],
                [t('docs.k8s.advanced.resources.medium'), '500m', '512Mi', t('docs.k8s.advanced.resources.mediumNote')],
                [t('docs.k8s.advanced.resources.large'), '1000m', '1Gi', t('docs.k8s.advanced.resources.largeNote')],
              ]}
            />
            <Callout type="tip">{t('docs.k8s.advanced.resources.tip')}</Callout>
          </SubSection>

          <SubSection id="tolerations" title={t('docs.k8s.advanced.tolerations.title')}>
            <P>{t('docs.k8s.advanced.tolerations.description')}</P>
            <CodeBlock title="Default tolerations (run on all nodes)">{`tolerations:
  - operator: Exists
    effect: NoSchedule
  - operator: Exists
    effect: NoExecute`}</CodeBlock>
            <P>{t('docs.k8s.advanced.tolerations.customize')}</P>
            <CodeBlock title="Restrict to worker nodes only">{`tolerations: []
nodeSelector:
  node-role.kubernetes.io/worker: ""`}</CodeBlock>
          </SubSection>
        </Section>

        {/* Vector Alternative */}
        <Section id="vector-alternative" title={t('docs.k8s.vector.title')}>
          <P>{t('docs.k8s.vector.description')}</P>
          <CodeBlock title="Vector DaemonSet: kubernetes_logs source">{`sources:
  k8s_logs:
    type: kubernetes_logs
    self_node_name: "\${VECTOR_SELF_NODE_NAME}"
    extra_label_selector: "app!=logpulse-agent"
    extra_namespace_label_selector: "kubernetes.io/metadata.name!=kube-system"

transforms:
  enrich:
    type: remap
    inputs:
      - k8s_logs
    source: |
      .index = "kubernetes"
      .source = string!(.kubernetes.pod_namespace) + "/" + string!(.kubernetes.pod_name)
      .sourcetype = "kubernetes"
      .fields.namespace = string!(.kubernetes.pod_namespace)
      .fields.pod = string!(.kubernetes.pod_name)
      .fields.container = string!(.kubernetes.container_name)
      .fields.node = string!(.kubernetes.pod_node_name)

sinks:
  logpulse:
    type: http
    inputs:
      - enrich
    uri: "https://api.logpulse.io/api/v1/ingest/vector"
    method: post
    encoding:
      codec: json
    auth:
      strategy: bearer
      token: "\${LOGPULSE_API_KEY}"
    batch:
      max_bytes: 1048576
      timeout_secs: 5`}</CodeBlock>
          <Callout type="info">{t('docs.k8s.vector.note')}</Callout>
        </Section>

        {/* Troubleshooting */}
        <Section id="troubleshooting" title={t('docs.k8s.troubleshooting.title')}>
          <P>{t('docs.k8s.troubleshooting.description')}</P>
          <Table
            headers={['Issue', 'Diagnosis', 'Solution']}
            rows={[
              [t('docs.k8s.troubleshooting.noLogs'), t('docs.k8s.troubleshooting.noLogsDiag'), t('docs.k8s.troubleshooting.noLogsFix')],
              [t('docs.k8s.troubleshooting.partial'), t('docs.k8s.troubleshooting.partialDiag'), t('docs.k8s.troubleshooting.partialFix')],
              [t('docs.k8s.troubleshooting.noMetadata'), t('docs.k8s.troubleshooting.noMetadataDiag'), t('docs.k8s.troubleshooting.noMetadataFix')],
              [t('docs.k8s.troubleshooting.highMem'), t('docs.k8s.troubleshooting.highMemDiag'), t('docs.k8s.troubleshooting.highMemFix')],
              [t('docs.k8s.troubleshooting.authFail'), t('docs.k8s.troubleshooting.authFailDiag'), t('docs.k8s.troubleshooting.authFailFix')],
              [t('docs.k8s.troubleshooting.crashLoop'), t('docs.k8s.troubleshooting.crashLoopDiag'), t('docs.k8s.troubleshooting.crashLoopFix')],
            ]}
          />
          <CodeBlock title="Useful debugging commands">{`# Check DaemonSet status
kubectl get ds -n logpulse

# View collector pod logs
kubectl logs -n logpulse -l app.kubernetes.io/name=logpulse-agent --tail=100

# Check pod events for errors
kubectl describe pod -n logpulse -l app.kubernetes.io/name=logpulse-agent

# Verify RBAC permissions
kubectl auth can-i list pods --as=system:serviceaccount:logpulse:logpulse-agent --all-namespaces

# Check Secret exists
kubectl get secret -n logpulse logpulse-agent-secret -o jsonpath='{.data.api-key}' | base64 -d

# View generated OTel config
kubectl get configmap -n logpulse logpulse-agent-config -o yaml`}</CodeBlock>
        </Section>

        {/* Uninstalling */}
        <Section id="uninstall" title={t('docs.k8s.uninstall.title')}>
          <P>{t('docs.k8s.uninstall.description')}</P>
          <CodeBlock title="Uninstall the LogPulse agent">{`# Remove the Helm release
helm uninstall logpulse-agent --namespace logpulse

# Optionally remove the namespace
kubectl delete namespace logpulse

# Verify cleanup (ClusterRole/ClusterRoleBinding are cluster-scoped)
kubectl get clusterrole | grep logpulse
kubectl get clusterrolebinding | grep logpulse`}</CodeBlock>
          <Callout type="info">{t('docs.k8s.uninstall.note')}</Callout>
        </Section>
      </div>

      {/* TOC sidebar */}
      <TableOfContents items={tocItems} />
    </div>
  );
}
